import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';

import { createQueryString } from '@/utils/queryUtils.ts';
import { canPublishResolvedBusinessProfile } from '@/app/common/businessProfile.ts';
import type React from 'react';
import getSearchParams from '../../common/getSearchParams';
import type { CuriosityParams } from '../../Curiosity';

export const enforceSearchId = (searchId: string, vcSearchId: string, businessProfile: string) => {
  const searchParams = getSearchParams();
  const hasNoSearchId = !searchParams[URLParameters.SEARCH_ID];
  const vcSearchIdShouldUpdate =
    vcSearchId !== '' ||
    (searchParams[URLParameters.VC_SEARCH_ID] && searchParams[URLParameters.VC_SEARCH_ID] !== vcSearchId);
  // MPH-960: this only ever rewrites a profile that arrived in the URL, so it must not publish one the
  // page did not actually receive — resolveBPV4 substitutes silently when the incoming profile has no
  // product for the search mode, and writing that back drops the partner while bf_source / utm_source
  // keep crediting them. canPublishResolvedBusinessProfile still allows the BP-split suffix through.
  const shouldUpdateBP =
    Boolean(searchParams[URLParameters.BUSINESS_PROFILE_ID]) &&
    searchParams[URLParameters.BUSINESS_PROFILE_ID] !== businessProfile &&
    canPublishResolvedBusinessProfile({
      resolved: businessProfile,
      fromUrl: searchParams[URLParameters.BUSINESS_PROFILE_ID],
    });

  if (hasNoSearchId) {
    searchParams[URLParameters.SEARCH_ID] = searchId;
  }
  if (vcSearchIdShouldUpdate) {
    searchParams[URLParameters.VC_SEARCH_ID] = vcSearchId;
  }
  if (shouldUpdateBP) {
    searchParams[URLParameters.BUSINESS_PROFILE_ID] = businessProfile;
  }
  if (hasNoSearchId || vcSearchIdShouldUpdate || shouldUpdateBP) {
    window.history.replaceState(null, '', `?${createQueryString(searchParams)}`);
  }
};

export const initialization = async ({
  listContainerRef,
  listView,
  isMobile,
  isDesktop,
  isTablet,
}: {
  searchParams: SearchParams;
  curiosityParams: CuriosityParams;
  listContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  listView: boolean | null;
  isMobile: boolean;
  isDesktop: boolean;
  isTablet: boolean;
}) => {
  if ((isMobile || isTablet) && !listView) {
    scrollList({
      listContainerRef,
      isMobile,
      isDesktop,
      isTablet,
    });
  }
};

export const scrollList = ({
  listContainerRef,
  isMobile,
  isDesktop,
  isTablet,
}: {
  listContainerRef: React.MutableRefObject<HTMLDivElement | undefined>;
  isMobile: boolean;
  isDesktop: boolean;
  isTablet: boolean;
}) => {
  const list = listContainerRef.current;
  const topMargin = list ? parseInt(window.getComputedStyle(list).marginTop, 10) : 0;
  const scrollToShowFirstResult = () => {
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  if (isMobile || isTablet) {
    scrollToShowFirstResult();
  } else if (isDesktop) {
    window.scrollTo({ top: topMargin, behavior: 'smooth' });
  }
};

export const scrollListToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

type ShowHorizontalFiltersProps = {
  isMobile: boolean;
  isTablet: boolean;
  listView: boolean | null;
  readyNoResults?: boolean;
};

export const showHorizontalFilters = ({
  isMobile,
  isTablet,
  listView,
  readyNoResults,
}: ShowHorizontalFiltersProps): boolean => {
  if (isMobile || isTablet) {
    return !readyNoResults;
  }
  return !listView;
};

export const canShowMap = ({ listView }: { listView: boolean | null }): boolean => !listView;

export const canShowList = ({
  isMobile,
  isTablet,
  listView,
}: {
  isMobile: boolean;
  isTablet: boolean;
  listView: boolean | null;
}): boolean | null => {
  if (isMobile || isTablet) {
    return listView;
  }
  return true;
};

export enum SEARCH_LAYOUT {
  MAP = 'map',
  LIST = 'list',
}
